/**
 * A KPI citation, rendered as a link into the file it came from.
 *
 * Clicking opens the SAME view a chat citation opens: `SpreadsheetViewer` for a
 * workbook, scrolled to and highlighting the cited cell or range, and `PdfViewer`
 * for a PDF, at its cited page. Both paths go through the existing `doppler://`
 * plumbing (`SpreadsheetViewerContext`, `PdfViewerContext`) — nothing about file
 * rendering is reimplemented here.
 *
 * Degrades to plain text, never to a dead link: a value whose row predates the
 * attachment-identity columns, a manual entry, or a file type the viewers do
 * not render still shows where it came from. A citation that cannot be opened
 * is worth strictly more as a sentence than as a control that does nothing.
 */

import { ExternalLink } from "lucide-react";
import React, { useState } from "react";

import { usePdfViewer } from "../../context/PdfViewerContext";
import { useSpreadsheetViewer } from "../../context/SpreadsheetViewerContext";
import type { KpiSourceRef } from "../../types/kpi";
import { openDopplerUri } from "../../utils/dopplerUri";
import {
	type KpiCitedNumber,
	kpiSourceHref,
	kpiSourceKind,
	kpiSourceLabel,
} from "./kpiSourceUri";

export interface KpiSourceLinkProps {
	source: KpiSourceRef;
	/** Link text; defaults to the full "file · sheet · ref" location. */
	label?: string;
	className?: string;
	/** The number this citation is for, so a PDF opens with it highlighted. */
	cited?: KpiCitedNumber | null;
}

const KpiSourceLink: React.FC<KpiSourceLinkProps> = ({
	source,
	label,
	className,
	cited,
}) => {
	const spreadsheetViewer = useSpreadsheetViewer();
	const pdfViewer = usePdfViewer();
	const [failed, setFailed] = useState(false);
	const href = kpiSourceHref(source, cited);
	const kind = kpiSourceKind(source);
	const text = label ?? kpiSourceLabel(source);

	if (href === null) {
		return (
			<span className={className} title="This file cannot be opened in place">
				{text}
			</span>
		);
	}

	const handleClick = async (event: React.MouseEvent<HTMLAnchorElement>) => {
		event.preventDefault();
		if (kind === "excel") {
			// The in-app viewer is the point — it highlights the cited cell. Only
			// if no viewer is mounted does this fall back to opening the file.
			const opened =
				spreadsheetViewer?.openSpreadsheetHref(href, {
					filename: source.file,
				}) ?? false;
			if (opened) return;
		}
		if (kind === "pdf") {
			// Same reasoning for a PDF: the modal keeps the reader on the grid and
			// lands on the cited page, where a new tab loses both.
			const opened =
				pdfViewer?.openPdfHref(href, { filename: source.file }) ?? false;
			if (opened) return;
		}
		try {
			await openDopplerUri(href);
		} catch {
			// The resolver logs; what matters here is that the user is told the
			// document did not open, rather than being left with a link that
			// silently does nothing.
			setFailed(true);
		}
	};

	return (
		<span className="inline-flex flex-wrap items-baseline gap-1">
			<a
				href={href}
				onClick={handleClick}
				className={
					className ??
					"break-words text-[#008782] underline decoration-dotted underline-offset-2 hover:text-[#004C4B]"
				}
				title={
					kind === "pdf"
						? `Open ${source.file}${source.page ? ` at page ${source.page}` : ""}`
						: `Open ${source.file}${source.sheet ? ` · ${source.sheet}` : ""}${source.ref ? `!${source.ref}` : ""}`
				}
			>
				{text}
				<ExternalLink
					className="ml-1 inline h-3 w-3 align-baseline text-text-muted"
					aria-hidden="true"
				/>
			</a>
			{failed && (
				<span className="text-xs text-red-600" role="alert">
					could not open this file
				</span>
			)}
		</span>
	);
};

export default KpiSourceLink;
